import { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useCustomerQuery } from '@order/graphql';

/**
 * Refetch Customer to access QRCode that is only generated after payment method.
 * This can't be a resolver update because adding a payment method doesn't return a QRCode.
 *
 * Additionally, when adding a payment method from elsewhere, like the account screen,
 * It would be premature to fetch the QRCode as we don't know if Scan To Pay will be used,
 * So if there are billing accounts but there isn't a QRCode, we refetch the customer on focus.
 */
export function useRefetchCustomerAfterPaymentMethodCreation() {
  const [customerResponse, refetchCustomer] = useCustomerQuery({
    requestPolicy: 'cache-only',
    pause: !useIsLoggedIn(),
  });

  const isLoadingQrCode = customerResponse?.fetching || customerResponse?.stale;
  const qrCode = customerResponse?.data?.currentCustomer?.qrCode;
  const hasBillingAccounts =
    (customerResponse?.data?.currentCustomer?.billingAccounts?.length ?? 0) > 0;
  const wasBillingAccountJustAdded = hasBillingAccounts && !qrCode;

  const onComplete = useCallback(() => {
    refetchCustomer({ requestPolicy: 'cache-and-network' });
  }, [refetchCustomer]);

  const fetchQrCodeAfterBillingAccountCreation = useCallback(() => {
    if (wasBillingAccountJustAdded) {
      refetchCustomer({ requestPolicy: 'cache-and-network' });
    }
  }, [wasBillingAccountJustAdded, refetchCustomer]);

  useFocusEffect(fetchQrCodeAfterBillingAccountCreation);

  return { isLoadingQrCode, qrCode, hasBillingAccounts, onComplete };
}
